
/* ============================== src/components/Modals/TaskModal/index.jsx ============================== */
// path: frontend/src/components/Modals/TaskModal/index.jsx
/**
 * Карточка задачи. RBAC-флаги берутся с сервера:
 *   - capabilities — глобальные (createTask, editRoles) из /auth/me;
 *   - task.permissions — per-task (canEdit, canChangeStatus) из TaskDto.
 *
 * canRestoreTask (бэкенд) для «Восстановить» не выносим отдельным флагом:
 * кнопка восстановления в TaskFooterActions проверяет capabilities.restore
 * + project.archived — сервер всё равно отклонит restore на архивном
 * проекте (TaskService.restore → canRestoreTask).
 *
 * db.tasks/db.projects/db.employees могут прийти массивом или конвертом
 * { items, meta } — нормализуем через unwrapItems (utils/dbShape.js).
 */
import { useCallback, useMemo, useEffect } from "react";

import { useDataHelpers } from "../../../hooks/useDataHelpers";
import { useTaskFormState } from "../../../hooks/useTaskFormState";
import { useTaskTemplate } from "../../../hooks/useTaskTemplate";
import { useTaskSubtasks } from "../../../hooks/useTaskSubtasks";
import { useTaskSelectOptions } from "../../../hooks/useTaskSelectOptions";
import { useTaskTimeLog } from "../../../hooks/useTaskTimeLog";
import { useTaskNotes } from "../../../hooks/useTaskNotes";
import { useTaskSave } from "../../../hooks/useTaskSave";
import { useTaskTabSync } from "../../../hooks/useTaskTabSync";
import { useEntityFiles } from "../../../hooks/useEntityFiles";
import {
  useChatUnreadCount,
  useChatTotalCount,
} from "../../../hooks/useChatStats";
import { useAuth } from "../../../hooks/useAuth";
import { useConfirm } from "../../../context/ConfirmContext";
import { useTaskQuery, useSubtasksQuery } from "../../../api/queries/tasks";

import { ModalShell } from "../../ModalShell";
import { EntityNotFoundModal } from "../EntityNotFoundModal";
import { Tabs } from "../../Tabs";
import Discussion from "../../Discussion";
import { FileManager } from "../../FileManager";
import { HistoryTab } from "../../HistoryTab";

import { TaskFormTab } from "./TaskFormTab";
import { TaskTimeTab } from "./TaskTimeTab";
import { TaskSubtasksTab } from "./TaskSubtasksTab";
import { TaskNotesTab } from "./TaskNotesTab";
import { TaskFooterActions } from "./TaskFooterActions";
import NoteEditorModal from "../NoteEditorModal";
import DailyNormPraiseModal from "../DailyNormPraiseModal";

import { isArchived } from "../../../utils/entityState";
import { buildApprovalHint } from "../../../utils/assignmentApproval";
import { buildAuthorSubtitle } from "../../../utils/entityAuthor";
import { unwrapItems } from "../../../utils/dbShape";

const TaskModal = (props) => {
  const { taskId, copyFromId, onClose, db } = props;

  const tasksArr = useMemo(() => unwrapItems(db?.tasks), [db?.tasks]);
  const existingInDb = taskId ? tasksArr.find((t) => t.id === taskId) : null;

  const { data: fetchedTask, isLoading: isTaskLoading } = useTaskQuery(taskId, {
    enabled: !!taskId,
  });
  const { data: fetchedCopySource } = useTaskQuery(copyFromId, {
    enabled: !!copyFromId,
  });
  const { data: fetchedSubtasks } = useSubtasksQuery(taskId, {
    enabled: !!taskId,
  });

  if (taskId && !isTaskLoading && !fetchedTask && !existingInDb) {
    return <EntityNotFoundModal entityType="task" onClose={onClose} />;
  }

  if (taskId && isTaskLoading && !fetchedTask) {
    return (
      <ModalShell title="Загрузка..." onClose={onClose} width={800}>
        <div className="modal-loading">Загрузка задачи...</div>
      </ModalShell>
    );
  }

  return (
    <TaskModalContent
      {...props}
      fetchedTask={fetchedTask}
      fetchedCopySource={fetchedCopySource}
      fetchedSubtasks={fetchedSubtasks}
    />
  );
};

const TaskModalContent = ({
  db,
  ur,
  taskId,
  initialTab = "form",
  parentTaskId,
  initialProjectId,
  returnToProjectId,
  returnToTaskId,
  returnToEmployeeTasksId,
  copyFromId,
  highlightFileId = null,
  highlightFolderId = null,
  onClose,
  onSave,
  onDelete,
  onChangeReq,
  openTask,
  openProject,
  openTag,
  toast,
  onCopy,
  onTabChange,
  returnToTag = null,
  fetchedTask,
  fetchedCopySource,
  fetchedSubtasks,
}) => {
  const { capabilities } = useAuth();
  const tasksArr = useMemo(() => unwrapItems(db?.tasks), [db?.tasks]);
  const projectsArr = useMemo(() => unwrapItems(db?.projects), [db?.projects]);
  const employeesArr = useMemo(
    () => unwrapItems(db?.employees),
    [db?.employees],
  );

  const normalizedDb = useMemo(
    () => ({
      ...db,
      tasks: tasksArr,
      projects: projectsArr,
      employees: employeesArr,
    }),
    [db, tasksArr, projectsArr, employeesArr],
  );

  const { empName, getTaskSpent } = useDataHelpers(normalizedDb);
  const { confirm } = useConfirm();

  const existing =
    fetchedTask ||
    (taskId ? tasksArr.find((t) => t.id === taskId) || null : null);
  const copySource =
    fetchedCopySource ||
    (copyFromId ? tasksArr.find((t) => t.id === copyFromId) || null : null);

  const isCopy = !existing && !!copySource;
  const isNew = !existing;
  const readOnly = !!(existing && isArchived(existing));

  const canEditFields =
    !readOnly &&
    (existing
      ? Boolean(existing.permissions?.canEdit)
      : capabilities.createTask);
  const canChangeStatus =
    !readOnly && !!existing && Boolean(existing.permissions?.canChangeStatus);
  const isAssignee = existing && existing.assigneeId === ur.id;
  const isAuthor = existing && existing.creatorId === ur.id;
  const isAdmin = Boolean(capabilities.editRoles);
  const canToggleFlags =
    !readOnly && !isAssignee && (isNew || isAuthor || isAdmin);
  const canLog = !readOnly && (existing ? isAssignee || isAdmin : true);
  const canCreateFromTask = Boolean(capabilities.createTask);
  const isProjectLocked = !!(initialProjectId || parentTaskId);

  const isViewOnlyViaTag = !!(
    existing &&
    returnToTag &&
    !canEditFields &&
    !isAssignee &&
    !isAuthor
  );

  const effectiveProjectId = useMemo(() => {
    if (initialProjectId) return initialProjectId;
    if (parentTaskId) {
      const parent = tasksArr.find((t) => t.id === parentTaskId);
      if (parent) return parent.projectId;
    }
    return "";
  }, [initialProjectId, parentTaskId, tasksArr]);

  const {
    values,
    handleChange,
    updateValues,
    handleSubmit,
    errors,
    touched,
    setFieldValue,
    isValid,
    isDirty,
    lockedField,
  } = useTaskFormState({
    existing,
    isCopy,
    copySource,
    isProjectLocked,
    effectiveProjectId,
    parentTaskId,
    db: normalizedDb,
    ur,
  });

  const {
    localFiles,
    localFolders,
    handleMoveFile,
    handleMoveFolder,
    handleRenameFile,
    handleFileUpload,
    handleFileDelete,
    handleCreateFolder,
    handleDeleteFolder,
  } = useEntityFiles({
    existing,
    ownerType: "task",
    toast,
    confirm,
  });

  const existingLogs = existing?.logs;
  const existingActualHours = existing?.actualHours;

  useEffect(() => {
    if (existingLogs && existingActualHours !== undefined) {
      updateValues({
        logs: existingLogs,
        actualHours: existingActualHours,
      });
    }
  }, [existingLogs, existingActualHours, updateValues]);

  const { appliedTemplateName, pendingTemplateSubtasks, applyTemplate } =
    useTaskTemplate({
      isCopy,
      copySource,
      db: normalizedDb,
      setFieldValue,
      isProjectLocked,
      effectiveProjectId,
      toast,
    });

  const { subtasks, draftSubtasks, displayedSubtasks } = useTaskSubtasks({
    db: normalizedDb,
    taskId: values.id,
    projectId: values.projectId,
    isNew,
    pendingTemplateSubtasks,
    serverSubtasks: existing ? (fetchedSubtasks ?? null) : null,
  });

  const project = projectsArr.find((p) => p.id === values.projectId);
  const isAdminProject = project && project.ptype === "admin";
  const isSummaryChecked =
    values.isSummary || subtasks.length > 0 || draftSubtasks.length > 0;
  const isSummaryDisabled =
    !canEditFields || subtasks.length > 0 || !canToggleFlags;
  const hasSubtasks =
    subtasks.length > 0 || values.isSummary || draftSubtasks.length > 0;

  const {
    projectOptions,
    assigneeOptionsList,
    priorityOptions,
    statusOptions,
    dependencyOptions,
    dependencyTypeOptions,
    candidates,
  } = useTaskSelectOptions({
    db: normalizedDb,
    ur,
    isProjectLocked,
    effectiveProjectId,
    taskId: values.id,
    projectId: values.projectId,
  });

  const {
    logHours,
    setLogHours,
    logNote,
    setLogNote,
    logDate,
    setLogDate,
    editingLogId,
    saveLog,
    startEdit,
    cancelEdit,
    deleteLog,
    praise,
    closePraise,
  } = useTaskTimeLog({
    existing,
    ur,
    toast,
  });

  const {
    notesList,
    editingNote,
    openNewNote,
    openExistingNote,
    closeNoteEditor,
    handleSaveNote,
    handleDeleteNote,
  } = useTaskNotes({ existing, ur, toast });

  const { saveHandler, saveAsDraftHandler, deleteHandler, isSaving } =
    useTaskSave({
      existing,
      isNew,
      onSave,
      onDelete,
      ur,
      toast,
      subtasks,
      draftSubtasks,
      pendingTemplateSubtasks,
    });

  const chatUnread = useChatUnreadCount(ur.id, values.projectId, values.id);
  const chatTotal = useChatTotalCount(values.projectId, values.id);

  const approvalHint = useMemo(
    () =>
      buildApprovalHint({
        user: ur,
        db: normalizedDb,
        existing,
        isNew,
        targetType: "task",
        entity: values,
      }),
    [ur, normalizedDb, existing, isNew, values],
  );

  const modalSubtitle = useMemo(
    () =>
      buildAuthorSubtitle({
        creatorId: existing?.creatorId,
        employees: employeesArr,
        label: "Задачу составил",
      }),
    [existing?.creatorId, employeesArr],
  );

  const tabs = [
    { id: "form", label: "Данные" },
    {
      id: "time",
      label: `Учёт времени (${getTaskSpent(values)}/${values.plannedHours ?? "-"})`,
    },
    ...(hasSubtasks
      ? [
          {
            id: "subtasks",
            label: `Подзадачи (${displayedSubtasks.length})`,
          },
        ]
      : []),
    ...(existing
      ? [
          {
            id: "chat",
            label: "Обсуждение",
            count: chatTotal,
            badge: chatUnread,
          },
          {
            id: "files",
            label: `Вложения (${localFiles.length})`,
          },
          { id: "hist", label: "История" },
        ]
      : []),
    ...(isAssignee && !readOnly
      ? [
          {
            id: "notes",
            label: `Заметки (${notesList.length})`,
          },
        ]
      : []),
  ];

  const { activeTab, handleTabChange, bodyRef } = useTaskTabSync(
    initialTab,
    onTabChange,
    tabs,
  );

  const showBackButton =
    returnToProjectId || returnToTaskId || returnToEmployeeTasksId;

  const saveDisabled =
    isSaving ||
    !(canEditFields || canChangeStatus) ||
    (isNew ? !isValid : !isValid || !isDirty);

  const modalTitle = readOnly ? (
    "Архивная задача - только чтение"
  ) : existing ? (
    <>
      Карточка задачи{" "}
      <span className="modal-title-entity">{`«${existing.title}»`}</span>
    </>
  ) : isCopy ? (
    <>
      Копирование задачи:{" "}
      <span className="modal-title-entity">{copySource.title}</span>
    </>
  ) : (
    "Новая задача"
  );

  const handleOpenProject = useCallback(
    (projectId) => {
      if (!openProject || !projectId) return;
      openProject(
        projectId,
        "info",
        null,
        "info",
        existing?.id || null,
        existing ? activeTab : null,
        returnToTag || null,
      );
    },
    [openProject, existing, activeTab, returnToTag],
  );

  const canRequestChange = !readOnly && existing && isAssignee && !!onChangeReq;
  const requestHours =
    canRequestChange && !values.isHourly
      ? () => onChangeReq("hours", "task", values.id)
      : null;
  const requestDeadline =
    canRequestChange && !values.isHourly
      ? () => onChangeReq("deadline", "task", values.id)
      : null;

  const goToSubtask = useCallback(
    (subtaskId) => {
      openTask(
        subtaskId,
        "form",
        subtaskId ? null : values.id,
        null,
        null,
        null,
        values.id,
        "subtasks",
        null,
        null,
        returnToTag || null,
      );
    },
    [openTask, values.id, returnToTag],
  );

  const handleSaveAsDraft = useCallback(() => {
    saveAsDraftHandler(values);
  }, [saveAsDraftHandler, values]);

  const footerActions = (
    <TaskFooterActions
      readOnly={readOnly}
      existing={existing}
      canCreateFromTask={canCreateFromTask}
      values={values}
      db={normalizedDb}
      toast={toast}
      onDelete={deleteHandler}
      onCopy={onCopy}
      disabled={isSaving}
      onSaveAsDraft={isNew ? handleSaveAsDraft : null}
      saveAsDraftDisabled={!values.title?.trim()}
    />
  );

  return (
    <>
      <ModalShell
        title={modalTitle}
        subtitle={modalSubtitle}
        onClose={onClose}
        width={800}
        className="modal-task"
        actions={footerActions}
        saveLabel={existing ? "Сохранить" : "Создать задачу"}
        saveDisabled={saveDisabled}
        onSave={handleSubmit(saveHandler)}
        bodyRef={bodyRef}
        showBack={!!showBackButton}
      >
        {readOnly && (
          <div className="info-box">
            {existing.archivedAt
              ? `Задача в архиве с ${existing.archivedAt}. Редактирование запрещено.`
              : `Задача ${
                  existing.status === "cancelled" ? "отменена" : "закрыта"
                }. Редактирование запрещено.`}
          </div>
        )}

        {isViewOnlyViaTag && (
          <div className="info-box">
            {`Открыто по метке «${returnToTag}» - режим просмотра.`}
          </div>
        )}

        <Tabs
          tabs={tabs}
          active={activeTab}
          onChange={handleTabChange}
          className="tabs-nowrap"
        />

        {activeTab === "form" && (
          <TaskFormTab
            form={{ values, handleChange, updateValues, touched, errors }}
            access={{
              canEditFields,
              isProjectLocked,
              isAdminProject,
              canToggleFlags,
            }}
            options={{
              projectOptions,
              assigneeOptionsList,
              priorityOptions,
              statusOptions,
              dependencyOptions,
              dependencyTypeOptions,
            }}
            template={{
              isNew,
              isCopy,
              appliedTemplateName,
              onApply: applyTemplate,
            }}
            summary={{
              checked: isSummaryChecked,
              disabled: isSummaryDisabled,
            }}
            onRequestHours={requestHours}
            onRequestDeadline={requestDeadline}
            lockedField={lockedField}
            approvalHint={approvalHint}
            onOpenProject={handleOpenProject}
            openTag={openTag}
          />
        )}

        {activeTab === "time" && (
          <TaskTimeTab
            spent={getTaskSpent(values)}
            planned={values.plannedHours}
            logs={values.logs}
            empName={empName}
            canLog={canLog}
            slot={
              values.isHourly
                ? { startTime: values.startTime, endTime: values.endTime }
                : null
            }
            logDate={logDate}
            setLogDate={setLogDate}
            logHours={logHours}
            setLogHours={setLogHours}
            logNote={logNote}
            setLogNote={setLogNote}
            editingLogId={editingLogId}
            onSave={saveLog}
            onStartEdit={startEdit}
            onCancelEdit={cancelEdit}
            onDelete={deleteLog}
            taskStartDate={values.start}
          />
        )}

        {activeTab === "subtasks" && hasSubtasks && (
          <TaskSubtasksTab
            tasks={displayedSubtasks}
            showCreateButton={!!existing && capabilities.createTask}
            readOnly={readOnly}
            onCreateSubtask={() => goToSubtask(null)}
            onRowClick={(id) => goToSubtask(id)}
            db={normalizedDb}
            getTaskSpent={getTaskSpent}
            empName={empName}
          />
        )}

        {activeTab === "chat" && (
          <Discussion
            projectId={values.projectId}
            taskId={values.id}
            currentUser={ur}
            candidates={candidates}
            toast={toast}
            employees={employeesArr}
          />
        )}

        {activeTab === "files" && existing && (
          <FileManager
            files={localFiles}
            folders={localFolders}
            highlightFileId={highlightFileId}
            highlightFolderId={highlightFolderId}
            onUpload={handleFileUpload}
            onDelete={handleFileDelete}
            onCreateFolder={handleCreateFolder}
            onDeleteFolder={handleDeleteFolder}
            onMoveFile={handleMoveFile}
            onMoveFolder={handleMoveFolder}
            onRenameFile={handleRenameFile}
            canUpload={!readOnly && (canEditFields || isAssignee || isAuthor)}
            canDelete={!readOnly && (canEditFields || isAssignee || isAuthor)}
            employeeName={empName}
          />
        )}

        {activeTab === "hist" && existing && (
          <HistoryTab history={existing.history} empName={empName} />
        )}

        {activeTab === "notes" && isAssignee && !readOnly && existing && (
          <TaskNotesTab
            notesList={notesList}
            onNewNote={openNewNote}
            onOpenNote={openExistingNote}
          />
        )}
      </ModalShell>

      {editingNote && (
        <NoteEditorModal
          note={editingNote}
          onSave={handleSaveNote}
          onDelete={handleDeleteNote}
          onClose={closeNoteEditor}
        />
      )}

      {praise && (
        <DailyNormPraiseModal date={praise.date} onClose={closePraise} />
      )}
    </>
  );
};

export { TaskModal };
export default TaskModal;
